import Component from '@glimmer/component';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { getOwner } from '@ember/owner';
import { t } from 'ember-intl';
import { CoreModalConfirm, Typography } from 'core/components';
import { boxPadding } from 'core/modifiers';

/**
 *
 * `IamGroupsModalsDeleteGroup` renders the modal for deleting an HCP Group from their organization
 *
 *
 * ```
 * <Iam::Groups::Modals::DeleteGroup
 *  @group={{@group}}
 *  @groupName='Engineering'
 *  @isActive={{this.isActive}}
 *  @closeModal={{this.closeModal}}
 * />
 * ```
 *
 * @class IamGroupsModalsDeleteGroup
 *
 */

/**
 * `groupName` is the name of the Group
 * @argument groupName
 * @type {String}
 */

/**
 * This shows or hides the modal based on truthiness.
 * @argument isActive
 * @type {Boolean}
 */

/**
 * A callback function to be called on a successful removal of a Group.
 * @argument onSuccess
 * @type {String}
 */
export default class IamGroupsModalsDeleteGroup extends Component {
  @service api;
  @service intl;
  @service flashMessages;
  @service router;
  @service userContext;

  // This task is required for Core::Modal::Confirm
  deleteGroup = task(async () => {
    try {
      await this.api.groups.groupsServiceDeleteGroup(
        this.args.group.resourceName
      );
      const successTitle = this.intl.t(
        'components.iam.groups.form.delete-group.success.title'
      );
      this.flashMessages.success(successTitle, {
        content: this.intl.t(
          'components.iam.groups.form.delete-group.success.message',
          {
            htmlSafe: true,
            groupName: this.args.groupName,
          }
        ),
      });

      //refresh the Groups list route to update the model
      const listRoute = 'cloud.access-control.groups.list';
      let route = getOwner(this).lookup(`route:${listRoute}`);
      route.refresh();

      await this.args.closeModal();

      // transition to the Groups list route
      return this.router.transitionTo('cloud.access-control.groups.list');
    } catch (err) {
      const errorTitle = this.intl.t(
        'components.iam.groups.form.delete-group.error.title'
      );
      this.flashMessages.error(errorTitle, {
        content: this.intl.t(
          'components.iam.groups.form.delete-group.error.message',
          {
            htmlSafe: true,
            groupName: this.args.groupName,
          }
        ),
      });
      throw err;
    }
  });

  <template>
    {{#if @isActive}}
      <CoreModalConfirm
        @headerColor="critical"
        @headerTagline={{t
          "components.iam.groups.modals.delete-group.header.tagline"
          groupName=@groupName
          htmlSafe=true
        }}
        @confirmText={{t "components.core.modal.confirm.delete"}}
        @headerIcon="alert-triangle"
        @headerText={{t
          "components.iam.groups.modals.delete-group.header.title"
        }}
        @confirmButtonColor="critical"
        @confirmButtonText={{t "components.iam.groups.form.delete-group.cta"}}
        @confirmHelp={{t
          "components.iam.groups.form.delete-group.confirm-help"
          htmlSafe=true
        }}
        @onSubmit={{this.deleteGroup}}
        @onCancel={{@closeModal}}
      >
        <:body>
          <div data-test-iam-groups-modal-delete-body>
            <Typography
              @variant="subtitle1"
              class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-primary"
              {{boxPadding "0 0 sm 0"}}
            >
              {{t
                "components.iam.groups.modals.delete-group.body.subtitle"
                groupName=@groupName
                htmlSafe=true
              }}
            </Typography>
            <Typography
              @variant="body1"
              class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
              {{boxPadding "0 0 md 0"}}
            >
              {{t
                "components.iam.groups.modals.delete-group.body.body1"
                htmlSafe=true
              }}
            </Typography>
            <Typography
              @variant="body2"
              class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
              {{boxPadding "0 0 md 0"}}
            >
              {{t
                "components.iam.groups.modals.delete-group.body.body2"
                htmlSafe=true
              }}
            </Typography>
          </div>
        </:body>
      </CoreModalConfirm>
    {{/if}}
  </template>
}
